import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { healNTrackApi, type HealNTrackPatientDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

/**
 * UC-M2-SAL-01 — IP Billing entry point. Looks up the UHID on the HealNTrack stub,
 * confirms admission state + ward, then routes the pharmacist to a customer-pre-filled
 * billing screen with billType=InPatient. (Full IP form polish lands in S10.)
 */
export function IpBillingPage() {
  const navigate = useNavigate();
  const [uhid, setUhid] = useState("");
  const [patient, setPatient] = useState<HealNTrackPatientDto | null>(null);

  const lookup = useMutation({
    mutationFn: () => healNTrackApi.patient(uhid.trim()),
    onSuccess: (p) => setPatient(p),
    onError: () => setPatient(null),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        <LocalHospitalIcon sx={{ verticalAlign: "middle", mr: 1 }} />
        In-patient billing
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Patient lookup</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField label="UHID *" value={uhid}
            onChange={(e) => setUhid(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") lookup.mutate(); }}
            placeholder="HN2026001847" sx={{ flex: 1, maxWidth: 360 }} size="small" />
          <Button variant="contained" startIcon={<SearchIcon />}
            disabled={!uhid.trim() || lookup.isPending}
            onClick={() => lookup.mutate()}>
            {lookup.isPending ? <CircularProgress size={16} color="inherit" /> : "Look up"}
          </Button>
        </Stack>
        {lookup.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(lookup.error)}</Alert>}
      </Paper>

      {patient && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6">{patient.fullName}</Typography>
            <Chip size="small" label={patient.gender} variant="outlined" />
            <Chip size="small" label={`DOB ${new Date(patient.dob).toLocaleDateString()}`} variant="outlined" />
            {patient.isAdmitted && (
              <Chip size="small" color="success"
                label={patient.wardCode ? `Admitted · ${patient.wardCode}` : "Admitted"} />
            )}
            {!patient.isAdmitted && (
              <Chip size="small" color="warning" label="Not currently admitted" />
            )}
          </Stack>
          <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 1 }}>
            <Stack><Typography variant="caption" color="text.secondary">UHID</Typography>
              <Typography sx={{ fontFamily: "monospace" }}>{patient.uhid}</Typography></Stack>
            <Stack><Typography variant="caption" color="text.secondary">Admission</Typography>
              <Typography>{patient.admissionId ?? "—"}</Typography></Stack>
            <Stack><Typography variant="caption" color="text.secondary">Branch</Typography>
              <Typography>{patient.branchCode ?? "—"}</Typography></Stack>
            <Stack><Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography>{patient.phone ?? "—"}</Typography></Stack>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" color="primary"
              disabled={!patient.isAdmitted}
              onClick={() => navigate("/sales/invoices/new",
                { state: { billType: "InPatient", uhid: patient.uhid, customerName: patient.fullName } })}>
              Start IP bill
            </Button>
          </Stack>
          {!patient.isAdmitted && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Patient is not currently admitted. Open the OP billing screen instead.
            </Alert>
          )}
        </Paper>
      )}

      <Alert severity="info">
        UHID lookup uses the HealNTrack stub (seeded UHIDs: HN2026001847, HN2026001848, HN2026001900).
        Final integrates with HealNTrack OAuth M2M per <code>INTEGRATION_CONTRACTS.md §1</code>.
      </Alert>
    </Box>
  );
}
